import * as THREE from "three";
import { RoundedBoxGeometry } from "three/addons/geometries/RoundedBoxGeometry.js";
import { mergeGeometries } from "three/addons/utils/BufferGeometryUtils.js";
import { GAME, totalHeight } from "../config/gameConfig";
import { VISUAL } from "../config/visualConfig";
import type { Game } from "../game/Game";
import { cellsFor, isSpanning, MAX_PIECE_CELLS } from "../game/Pieces";
import type { Layer } from "../game/types";
import { layerSeparation, layerZ } from "./layerLayout";
import { SolidBlockMaterial, solidBlockGeometry } from "./SolidBlockMaterial";
import { ledMaterial } from "./ledMaterial";

type LayerVisual = {
  group: THREE.Group;
  frame: THREE.LineSegments;
  locked: THREE.InstancedMesh;
  glow: THREE.InstancedMesh;
  scanner: THREE.Mesh;
  flash: number;
};

export class BoardRenderer {
  readonly group = new THREE.Group();
  private readonly layers: Record<Layer, LayerVisual>;
  private readonly activeGroup = new THREE.Group();
  private readonly activeBlocks: THREE.Mesh[] = [];
  private readonly activeShells: THREE.Mesh[] = [];
  private readonly activeHalos: THREE.Sprite[] = [];
  private readonly glowTexture = this.createGlowTexture();
  private readonly showcase = new THREE.Group();
  private readonly showcaseLayers: { layer: Layer; blocks: THREE.InstancedMesh; wires: THREE.InstancedMesh }[] = [];
  private readonly blockMaterials: SolidBlockMaterial[] = [];
  private readonly spanningBody = new THREE.InstancedMesh(
    solidBlockGeometry(VISUAL.layerDepth - 0.72), this.createBlockMaterial(), GAME.width * GAME.visibleHeight,
  );
  private readonly spanningWires = new THREE.InstancedMesh(
    this.createWireGeometry(),
    new THREE.MeshBasicMaterial({ color: VISUAL.spanning, transparent: true, opacity: 0.7, toneMapped: false, blending: THREE.AdditiveBlending, depthWrite: false }),
    GAME.width * GAME.visibleHeight,
  );
  private transitionFrom: Layer | null = null;
  private feedback = 0;
  private elapsed = 0;
  private collapseSeconds: number | null = null;
  private readonly ledMaterials: THREE.ShaderMaterial[] = [];
  private ledScrollTime = 0;
  private ledBoostRemaining = 0;

  constructor(private readonly game: Game) {
    this.layers = {
      back: this.createLayer("back", VISUAL.back),
      front: this.createLayer("front", VISUAL.front),
    };
    this.group.add(this.layers.back.group, this.layers.front.group, this.activeGroup);
    this.activeGroup.name = "active-piece";
    this.createActiveBlocks();
    this.createDepthRails();
    this.createShowcase();
    for (const pool of [this.spanningBody, this.spanningWires]) {
      pool.count = 0;
      pool.frustumCulled = false;
      pool.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      this.group.add(pool);
    }
  }

  update(deltaSeconds: number): void {
    this.elapsed += deltaSeconds;
    for (const material of this.blockMaterials) material.sweepTime.value = this.elapsed;
    const speed = 1 + 9 * THREE.MathUtils.smoothstep(this.ledBoostRemaining, 0, 0.25);
    this.ledScrollTime += deltaSeconds * speed;
    this.ledBoostRemaining = Math.max(0, this.ledBoostRemaining - deltaSeconds);
    for (const material of this.ledMaterials) material.uniforms.time!.value = material.uniforms.horizontal!.value ? this.elapsed : this.ledScrollTime;
    this.feedback *= Math.exp(-deltaSeconds * 18);
    this.showcase.visible = this.game.phase === "title";
    const activeLayer = this.game.foregroundLayer;
    for (const layer of ["front", "back"] as const) {
      const visual = this.layers[layer];
      visual.group.position.z = this.layerZ(layer);
      visual.glow.renderOrder = layer === activeLayer ? 3 : 1;
      const active = (layer === activeLayer || Boolean(this.game.active && isSpanning(this.game.active.type))) && this.game.phase === "playing";
      visual.scanner.position.y = ((this.elapsed * 1.8 + (layer === "back" ? 7 : 0)) % 14) - 7;
      const material = visual.frame.material as THREE.LineBasicMaterial;
      const targetOpacity = active ? 0.065 : 0.025;
      material.opacity += (targetOpacity - material.opacity) * Math.min(1, deltaSeconds * 12);
      if (visual.flash > 0) {
        visual.flash -= deltaSeconds;
        // Feedback lights the blocks; empty cells stay quiet even at impact.
        material.opacity = Math.max(material.opacity, 0.1 * Math.sin(visual.flash * 45) ** 2);
      }
      this.updateLocked(layer);
      const blocks = visual.locked.material as THREE.MeshStandardMaterial;
      const fade = this.collapseSeconds === null ? 1 : 1 - THREE.MathUtils.smoothstep(this.collapseSeconds, 3.8, VISUAL.gameOverSeconds);
      const opacity = this.layerOpacity(layer) * fade;
      this.setBlockOpacity(blocks, opacity);
      blocks.emissiveIntensity = 0.14 + Math.max(0, visual.flash) * 1.5;
      (visual.glow.material as THREE.MeshBasicMaterial).opacity = 0.85 * opacity;
    }

    for (const { layer, blocks, wires } of this.showcaseLayers) {
      const opacity = this.layerOpacity(layer);
      this.setBlockOpacity(blocks.material as THREE.Material, opacity);
      (wires.material as THREE.Material).opacity = 0.85 * opacity;
    }

    this.updateActive();
    this.updateSpanningLinks();
  }

  pulseLayer(layer: Layer): void {
    this.layers[layer].flash = 0.22;
  }

  setCollapse(seconds: number | null): void { this.collapseSeconds = seconds; }

  react(strength: number): void { this.feedback = Math.max(this.feedback, strength); }

  boostLeds(lines: number): void {
    this.ledBoostRemaining = Math.max(this.ledBoostRemaining, 1.8 + Math.min(4, lines) * 0.14);
  }

  beginLayerTransition(from: Layer): void {
    this.transitionFrom = from;
  }

  resetLayerTransition(): void { this.transitionFrom = null; this.ledBoostRemaining = 0; }

  private createLayer(layer: Layer, color: number): LayerVisual {
    const group = new THREE.Group();
    group.name = layer === "front" ? "cyan-board" : "red-board";
    group.position.z = this.layerZ(layer);

    const frame = this.createFrame(color);
    const count = GAME.width * GAME.visibleHeight;
    const geometry = solidBlockGeometry();
    const material = this.createBlockMaterial();
    const locked = new THREE.InstancedMesh(geometry, material, count);
    locked.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    locked.frustumCulled = false;

    const glowMaterial = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.85,
      toneMapped: false,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    // A clean inset face outline, without triangle diagonals over every block.
    const outlineGeometry = this.createWireGeometry();
    const glow = new THREE.InstancedMesh(outlineGeometry, glowMaterial, count);
    glow.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    glow.frustumCulled = false;
    glow.renderOrder = layer === "back" ? 1 : 3;

    const backing = new THREE.Mesh(
      new THREE.PlaneGeometry(GAME.width, GAME.visibleHeight),
      new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.025, depthWrite: false, side: THREE.DoubleSide }),
    );
    backing.position.z = -0.18;
    const scanner = new THREE.Mesh(new THREE.PlaneGeometry(GAME.width, 0.09), new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.16, depthWrite: false, blending: THREE.AdditiveBlending }));
    scanner.position.z = -0.4;

    group.add(backing, locked, glow, frame, scanner, this.createHousing(color));
    return { group, frame, locked, glow, scanner, flash: 0 };
  }

  private createGlowTexture(): THREE.CanvasTexture {
    const canvas = document.createElement("canvas"); canvas.width = 128; canvas.height = 128;
    const context = canvas.getContext("2d")!;
    const gradient = context.createRadialGradient(64, 64, 8, 64, 64, 64);
    gradient.addColorStop(0, "rgba(255,255,255,0.65)");
    gradient.addColorStop(0.4, "rgba(255,255,255,0.25)");
    gradient.addColorStop(1, "rgba(255,255,255,0)");
    context.fillStyle = gradient; context.fillRect(0, 0, 128, 128);
    return new THREE.CanvasTexture(canvas);
  }

  private createWireGeometry(): THREE.BufferGeometry {
    const parts: THREE.BufferGeometry[] = [];
    const half = VISUAL.cellSize / 2;
    for (const a of [-1, 1]) for (const b of [-1, 1]) {
      parts.push(new THREE.BoxGeometry(VISUAL.cellSize, 0.028, 0.028).translate(0, a * half, b * 0.36));
      parts.push(new THREE.BoxGeometry(0.028, VISUAL.cellSize, 0.028).translate(a * half, 0, b * 0.36));
      parts.push(new THREE.BoxGeometry(0.028, 0.028, 0.72).translate(a * half, b * half, 0));
    }
    const merged = mergeGeometries(parts)!;
    for (const part of parts) part.dispose();
    return merged;
  }

  private createShowcase(): void {
    const geometry = solidBlockGeometry();
    for (const layer of ["front", "back"] as const) {
      const blocks = new THREE.InstancedMesh(geometry, this.createBlockMaterial(), 24);
      const wires = new THREE.InstancedMesh(this.createWireGeometry(), new THREE.MeshBasicMaterial({ color: layer === "front" ? VISUAL.front : VISUAL.back, transparent: true, opacity: 0.85, toneMapped: false, depthWrite: false, blending: THREE.AdditiveBlending }), 24);
      const matrix = new THREE.Matrix4();
      const color = new THREE.Color();
      let count = 0;
      for (let x = 0; x < GAME.width; x++) {
        const height = layer === "front" ? [2, 3, 2, 1, 2, 1, 2, 3][x]! : [3, 2, 3, 2, 1, 2, 3, 2][x]!;
        for (let y = 0; y < height; y++) {
          blocks.setMatrixAt(count, matrix.makeTranslation(this.worldX(x), -6.5 + y, this.layerZ(layer)));
          wires.setMatrixAt(count, matrix);
          blocks.setColorAt(count++, color.setHex(layer === "front" ? VISUAL.front : VISUAL.back));
        }
      }
      blocks.count = count;
      wires.count = count;
      this.showcase.add(blocks, wires);
      this.showcaseLayers.push({ layer, blocks, wires });
    }
    this.group.add(this.showcase);
  }

  private createHousing(color: number): THREE.Group {
    const housing = new THREE.Group();
    const metal = new THREE.MeshStandardMaterial({ color: 0x142638, metalness: 0.8, roughness: 0.32 });
    const verticalLight = ledMaterial(color, false);
    const horizontalLight = ledMaterial(color, true);
    this.ledMaterials.push(verticalLight, horizontalLight);
    const rails: THREE.BufferGeometry[] = [];
    const verticalStrips: THREE.BufferGeometry[] = [];
    const horizontalStrips: THREE.BufferGeometry[] = [];
    for (const x of [-GAME.width / 2 - 0.14, GAME.width / 2 + 0.14]) {
      rails.push(new RoundedBoxGeometry(0.2, 14.46, 0.38, 2, 0.035).translate(x, 0, 0));
      for (const z of [-0.2, 0.2]) {
        const strip = new THREE.BoxGeometry(0.07, 13.9, 0.025).translate(x, 0, z);
        verticalStrips.push(strip);
      }
    }
    for (const y of [-7.14, 7.14]) {
      rails.push(new RoundedBoxGeometry(GAME.width + 0.48, 0.18, 0.38, 2, 0.035).translate(0, y, 0));
      for (const z of [-0.2, 0.2]) horizontalStrips.push(new THREE.BoxGeometry(GAME.width - 0.08, 0.035, 0.025).translate(0, y, z));
    }
    const vertical = new THREE.Mesh(mergeGeometries(verticalStrips)!, verticalLight);
    const horizontal = new THREE.Mesh(mergeGeometries(horizontalStrips)!, horizontalLight);
    vertical.name = "vertical-led-wave";
    horizontal.name = "horizontal-led-pulse";
    housing.add(new THREE.Mesh(mergeGeometries(rails)!, metal), vertical, horizontal);
    for (const geometry of [...rails, ...verticalStrips, ...horizontalStrips]) geometry.dispose();
    const bolts = new THREE.InstancedMesh(new THREE.CylinderGeometry(0.045, 0.045, 0.025, 6), metal, 4);
    const matrix = new THREE.Matrix4();
    let index = 0;
    for (const x of [-GAME.width / 2 - 0.14, GAME.width / 2 + 0.14]) for (const y of [-7.14, 7.14]) {
      matrix.makeRotationX(Math.PI / 2); matrix.setPosition(x, y, 0.21);
      bolts.setMatrixAt(index++, matrix);
    }
    housing.add(bolts);
    return housing;
  }

  private createDepthRails(): void {
    const metal = new THREE.MeshStandardMaterial({ color: 0x273549, metalness: 0.8, roughness: 0.3 });
    const geometry = new RoundedBoxGeometry(0.12, 0.12, VISUAL.layerDepth + 0.38, 2, 0.025);
    const rails = new THREE.InstancedMesh(geometry, metal, 4);
    let i = 0;
    const matrix = new THREE.Matrix4();
    for (const x of [-GAME.width / 2 - 0.14, GAME.width / 2 + 0.14]) for (const y of [-7.14, 7.14]) {
      rails.setMatrixAt(i++, matrix.makeTranslation(x, y, 0));
    }
    this.group.add(rails);
  }

  private createFrame(color: number): THREE.LineSegments {
    const left = -GAME.width / 2;
    const right = GAME.width / 2;
    const top = GAME.visibleHeight / 2;
    const bottom = -GAME.visibleHeight / 2;
    const points: number[] = [];
    const segment = (x1: number, y1: number, x2: number, y2: number): void => {
      points.push(x1, y1, 0, x2, y2, 0);
    };
    segment(left, top, right, top);
    segment(right, top, right, bottom);
    segment(right, bottom, left, bottom);
    segment(left, bottom, left, top);
    for (let x = 1; x < GAME.width; x += 1) segment(left + x, top, left + x, bottom);
    for (let y = 1; y < GAME.visibleHeight; y += 1) segment(left, top - y, right, top - y);
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.Float32BufferAttribute(points, 3));
    const material = new THREE.LineBasicMaterial({
      color,
      transparent: true,
      opacity: 0.065,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const frame = new THREE.LineSegments(geometry, material);
    frame.renderOrder = 4;
    return frame;
  }

  private createActiveBlocks(): void {
    const geometry = solidBlockGeometry();
    for (let i = 0; i < MAX_PIECE_CELLS; i += 1) {
      const block = new THREE.Mesh(
        geometry,
        this.createBlockMaterial(),
      );
      const shell = new THREE.Mesh(
        this.createWireGeometry(),
        new THREE.MeshBasicMaterial({
          transparent: true,
          opacity: 0.95,
          toneMapped: false,
          depthWrite: false,
          blending: THREE.AdditiveBlending,
        }),
      );
      const halo = new THREE.Sprite(new THREE.SpriteMaterial({ map: this.glowTexture, transparent: true, opacity: 0.28, blending: THREE.AdditiveBlending, depthWrite: false }));
      halo.scale.set(1.8, 1.8, 1);
      this.activeHalos.push(halo);
      this.activeBlocks.push(block);
      this.activeShells.push(shell);
      this.activeGroup.add(block, shell, halo);
    }
  }

  private updateLocked(layer: Layer): void {
    const visual = this.layers[layer];
    const board = this.game.boards[layer];
    const matrix = new THREE.Matrix4();
    const color = new THREE.Color();
    const rotation = new THREE.Quaternion();
    const position = new THREE.Vector3();
    const scale = new THREE.Vector3(1, 1, 1);
    const axis = new THREE.Vector3(0, 0, 1);
    let count = 0;
    for (let y = GAME.hiddenRows; y < totalHeight; y += 1) {
      for (let x = 0; x < GAME.width; x += 1) {
        const type = board.cells[y]?.[x];
        if (!type) continue;
        // Different delays break up the pile without modifying game cells.
        const delay = (totalHeight - 1 - y) * 0.045 + (x % 3) * 0.09 + (layer === "back" ? 0.16 : 0);
        const fall = this.collapseSeconds === null ? 0 : Math.max(0, this.collapseSeconds - delay);
        const sink = Math.min(24, fall * fall * 0.85);
        position.set(this.worldX(x) + Math.sin(x * 9 + y) * fall * 0.045, this.worldY(y) - sink, 0);
        rotation.setFromAxisAngle(axis, Math.sin(x * 5 + y * 3) * fall * 0.085);
        matrix.compose(position, rotation, scale);
        visual.locked.setMatrixAt(count, matrix);
        color.setHex(isSpanning(type) ? VISUAL.spanning : layer === "front" ? VISUAL.front : VISUAL.back);
        visual.locked.setColorAt(count, color);
        visual.glow.setMatrixAt(count, matrix);
        visual.glow.setColorAt(count, color);
        count += 1;
      }
    }
    visual.locked.count = count;
    visual.glow.count = count;
    visual.locked.instanceMatrix.needsUpdate = true;
    if (visual.locked.instanceColor) visual.locked.instanceColor.needsUpdate = true;
    visual.glow.instanceMatrix.needsUpdate = true;
    if (visual.glow.instanceColor) visual.glow.instanceColor.needsUpdate = true;
  }

  private updateSpanningLinks(): void {
    let count = 0;
    const matrix = new THREE.Matrix4();
    const separation = layerSeparation(this.game);
    const color = new THREE.Color(VISUAL.spanning);
    if (this.collapseSeconds === null && separation > 0.72) for (let y = GAME.hiddenRows; y < totalHeight; y++) {
      for (let x = 0; x < GAME.width; x++) {
        const type = this.game.boards.front.cells[y]![x];
        if (!type || !isSpanning(type) || !isSpanning(this.game.boards.back.cells[y]![x] ?? "I")) continue;
        matrix.makeScale(1, 1, (separation - 0.72) / (VISUAL.layerDepth - 0.72));
        matrix.setPosition(this.worldX(x), this.worldY(y), 0);
        this.spanningBody.setMatrixAt(count, matrix);
        this.spanningBody.setColorAt(count, color);
        matrix.makeScale(1, 1, separation / 0.72);
        matrix.setPosition(this.worldX(x), this.worldY(y), 0);
        this.spanningWires.setMatrixAt(count++, matrix);
      }
    }
    for (const pool of [this.spanningBody, this.spanningWires]) {
      pool.count = count;
      pool.instanceMatrix.needsUpdate = true;
      if (pool.instanceColor) pool.instanceColor.needsUpdate = true;
    }
  }

  private updateActive(): void {
    const piece = this.game.active;
    const visible = Boolean(piece && (this.game.phase === "playing" || this.game.phase === "paused"));
    this.activeGroup.visible = visible;
    if (!piece) return;
    const spanning = isSpanning(piece.type);

    const progress = this.transitionFrom === null ? 1 : this.game.layerSwitchProgress;
    // The falling piece stays at the controls' foreground; the boards swap
    // underneath it. Its destination and color belong to the new board.
    const activeZ = VISUAL.layerDepth / 2;
    const spanningDepth = layerSeparation(this.game) + 0.72;
    const cells = cellsFor(piece.type, piece.rotation);
    const color = new THREE.Color(spanning ? VISUAL.spanning : piece.layer === "front" ? VISUAL.front : VISUAL.back);
    if (!spanning && this.transitionFrom !== null) color.lerpColors(
      new THREE.Color(this.transitionFrom === "front" ? VISUAL.front : VISUAL.back), color.clone(),
      THREE.MathUtils.smoothstep(progress, 0.3, 0.85),
    );
    if (progress >= 1) this.transitionFrom = null;
    for (let i = 0; i < this.activeBlocks.length; i += 1) {
      const cell = cells[i];
      const block = this.activeBlocks[i]!;
      const shell = this.activeShells[i]!;
      const halo = this.activeHalos[i]!;
      if (!cell) {
        block.visible = shell.visible = halo.visible = false;
        continue;
      }
      const x = piece.x + cell.x;
      const y = piece.y + cell.y;
      const enteredVisibleBoard = y >= GAME.hiddenRows;
      block.visible = enteredVisibleBoard;
      shell.visible = enteredVisibleBoard;
      halo.visible = enteredVisibleBoard;
      block.position.set(this.worldX(x), this.worldY(y), spanning ? 0 : activeZ);
      block.scale.setScalar(1 + this.feedback * 0.07);
      if (spanning) block.scale.z *= spanningDepth / 0.72;
      shell.position.copy(block.position);
      shell.scale.copy(block.scale);
      halo.position.copy(block.position); halo.position.z += spanning ? spanningDepth / 2 + 0.04 : 0.1;
      halo.material.color.copy(color);
      halo.material.opacity = 0.22 + this.feedback * 0.2;
      const blockMaterial = block.material as THREE.MeshStandardMaterial;
      blockMaterial.color.copy(color);
      blockMaterial.emissiveIntensity = 0.22 + this.feedback * 0.45 + Math.sin(this.elapsed * 3) * 0.04;
      (shell.material as THREE.MeshBasicMaterial).color.copy(color);
    }
  }

  private layerZ(layer: Layer): number {
    return layerZ(this.game, layer);
  }

  private layerOpacity(layer: Layer): number {
    // Follow physical depth throughout the anticipated swap, including reversal.
    const frontness = THREE.MathUtils.clamp(this.layerZ(layer) / VISUAL.layerDepth + 0.5, 0, 1);
    return THREE.MathUtils.lerp(VISUAL.rearBlockOpacity, 1, frontness);
  }

  private setBlockOpacity(material: THREE.Material, opacity: number): void {
    material.opacity = opacity;
    const transparent = opacity < 1;
    if (material.transparent !== transparent) {
      material.transparent = transparent;
      material.depthWrite = !transparent;
      material.needsUpdate = true;
    }
  }

  private createBlockMaterial(): SolidBlockMaterial {
    const material = new SolidBlockMaterial();
    this.blockMaterials.push(material);
    return material;
  }

  private worldX(column: number): number {
    return column - GAME.width / 2 + 0.5;
  }

  private worldY(row: number): number {
    return GAME.visibleHeight / 2 - (row - GAME.hiddenRows) - 0.5;
  }
}
